/**
 * Ricerca esercizi lato client — parte pura, condivisa.
 *
 * Il catalogo cerca via RPC `search_exercise_ids` (nome + alias, normalizzati),
 * mentre Builder ed esecuzione filtravano con un `includes` sul solo nome: i
 * 177 alias in produzione non arrivavano proprio dove l'utente li digita
 * ("RDL", "lat machine"). Questo modulo unifica il match e aggiunge il
 * riconoscimento per token (l'ordine delle parole non conta piu').
 *
 * `normalizeExerciseName` e' il gemello JS di `public.normalize_exercise_name`
 * (migration 20260701): stesse regole, stesso risultato. Se cambia la funzione
 * SQL va aggiornata anche questa, altrimenti client e server smettono di
 * concordare su cosa significa "matcha".
 */

// Le stesse coppie di `translate()` nella funzione SQL. I caratteri accentati
// non elencati (es. ñ, ç) cadono nella regex e diventano spazio, come nel DB.
const ACCENTED = 'àáâãäèéêëìíîïòóôõöùúûü';
const PLAIN = 'aaaaaeeeeiiiiooooouuuu';

/** Query piu' corta di cosi' mostra il catalogo intero invece di filtrare. */
export const MIN_QUERY_LENGTH = 2;

// Punteggi di match, dal piu' forte al piu' debole. L'ordinamento del dropdown
// dipende da questi valori: il nome esatto sta sempre sopra un alias.
export const NO_MATCH = 0;
export const MATCH_ALIAS = 1;
export const MATCH_TOKENS = 2;
export const MATCH_PREFIX = 3;
export const MATCH_EXACT = 4;

/**
 * Minuscolo, accenti rimossi, tutto cio' che non e' [a-z0-9] diventa spazio,
 * spazi collassati. Identica a public.normalize_exercise_name.
 */
export const normalizeExerciseName = (value) => {
  const lowered = String(value ?? '').toLowerCase();

  let translated = '';
  for (const char of lowered) {
    const index = ACCENTED.indexOf(char);
    translated += index === -1 ? char : PLAIN[index];
  }

  return translated.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
};

/**
 * Quanto forte matcha un nome. `aliasMatch` arriva dalla RPC: vale solo come
 * ultima spiaggia, perche' un match sul nome e' sempre piu' informativo.
 */
export const scoreExerciseMatch = (name, query, { aliasMatch = false } = {}) => {
  const normalizedQuery = normalizeExerciseName(query);
  if (!normalizedQuery) return NO_MATCH;

  const normalizedName = normalizeExerciseName(name);
  if (normalizedName === normalizedQuery) return MATCH_EXACT;
  if (normalizedName.startsWith(normalizedQuery)) return MATCH_PREFIX;

  // Tutti i token devono comparire, in qualsiasi ordine: "panca bilanciere"
  // trova "Panca Piana Bilanciere", che `includes` mancava.
  const tokens = normalizedQuery.split(' ');
  if (tokens.every((token) => normalizedName.includes(token))) return MATCH_TOKENS;

  return aliasMatch ? MATCH_ALIAS : NO_MATCH;
};

/**
 * Filtra e ordina i template. `aliasIds` sono gli id trovati dalla RPC per la
 * stessa query (Set o array): se manca — offline, RPC non disponibile — il
 * filtro resta quello locale e la ricerca non regredisce mai.
 */
export const filterExerciseTemplates = (templates, query, aliasIds) => {
  const list = Array.isArray(templates) ? templates : [];
  if (normalizeExerciseName(query).length < MIN_QUERY_LENGTH) return list;

  const ids = aliasIds instanceof Set ? aliasIds : new Set(aliasIds || []);

  return list
    .map((template) => ({
      template,
      score: scoreExerciseMatch(template?.name, query, {
        aliasMatch: ids.has(template?.id),
      }),
    }))
    .filter((entry) => entry.score > NO_MATCH)
    .sort(
      (a, b) =>
        b.score - a.score ||
        String(a.template?.name ?? '').localeCompare(String(b.template?.name ?? ''), 'it')
    )
    .map((entry) => entry.template);
};
